import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { useEmailCampaigns, useEmailOverview } from '../hooks/email';
import { useDiscoveryPaths } from '../workspace/paths';
import { CheckboxField, Note } from '../workspace/ui';
import { funnelProblems, rate, sequenceStages } from './model';
import type { SequenceProgress } from './types';
import { BreakdownTable, FunnelStrip, when, SequenceBar } from './parts';

/** Every email campaign with its funnel, and how segments compare across all of them. */
export function CampaignsTab(): JSX.Element {
  const paths = useDiscoveryPaths();
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading, error } = useEmailCampaigns(showArchived);
  const campaigns = data?.campaigns ?? [];
  const hasInstantly = campaigns.some((c) => c.provider === 'instantly');
  const overview = useEmailOverview(campaigns.length > 0).data;

  if (error) {
    return (
      <Note tone="error" role="alert">
        Couldn't load campaigns: {(error as Error).message}
      </Note>
    );
  }
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading campaigns…</p>;

  if (campaigns.length === 0 && !showArchived) {
    return (
      <div className="panel rounded-xl">
        <EmptyState
          icon={<Mail className="h-7 w-7" />}
          title="No email campaigns yet"
          hint="Pick a segment on the Audience tab and send it to an Instantly campaign, or export it. It will show up here with its results."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {data && hasInstantly && (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">All campaigns</h2>
          <FunnelStrip funnel={data.totals} provider="instantly" />
        </section>
      )}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold">Campaigns</h2>
          <CheckboxField checked={showArchived} onChange={setShowArchived}>
            Show archived
          </CheckboxField>
        </div>
        <DataTable>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>Campaign</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Added</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Sent</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Opened</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Replied</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Joined</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Posted</DataTableHeaderCell>
              <DataTableHeaderCell className="min-w-[11rem]">Sequence</DataTableHeaderCell>
              <DataTableHeaderCell>Updated</DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {campaigns.map((c) => {
              const f = c.funnel;
              const csv = c.provider === 'csv';
              const problems = funnelProblems(f);
              return (
                <DataTableRow key={c.publicId} state={c.archivedAt ? 'terminal' : 'default'}>
                  <DataTableCell anchor data-label="Campaign">
                    <Link to={paths.emailCampaign(c.publicId)} className="hover:underline">
                      {c.name}
                    </Link>
                    <div className="text-sm font-normal text-muted-foreground">
                      {csv ? 'CSV export' : 'Instantly'}
                      {c.archivedAt && ' · archived'}
                      {problems.length > 0 && <span className="text-pending-strong"> · needs attention</span>}
                    </div>
                  </DataTableCell>
                  <DataTableCell numeric data-label="Added">{f.added.toLocaleString()}</DataTableCell>
                  <DataTableCell numeric data-label="Sent" muted={csv}>{csv ? '—' : f.sent.toLocaleString()}</DataTableCell>
                  <DataTableCell numeric data-label="Opened" muted={csv}>{csv ? '—' : `${f.opened.toLocaleString()} · ${rate(f.opened, f.sent)}`}</DataTableCell>
                  <DataTableCell numeric data-label="Replied" muted={csv}>{csv ? '—' : `${f.replied.toLocaleString()} · ${rate(f.replied, f.sent)}`}</DataTableCell>
                  <DataTableCell numeric data-label="Joined">
                    {f.joined.toLocaleString()} · {rate(f.joined, csv ? f.added : f.sent)}
                  </DataTableCell>
                  <DataTableCell numeric data-label="Posted">
                    {(f.posted ?? 0).toLocaleString()} · {rate(f.posted ?? 0, csv ? f.added : f.sent)}
                  </DataTableCell>
                  <DataTableCell data-label="Sequence" muted={csv || !c.progress}>
                    {csv || !c.progress ? '—' : <SequenceSummary progress={c.progress} stepCount={c.stepCount} />}
                  </DataTableCell>
                  <DataTableCell muted data-label="Updated">{csv ? when(c.createdAt) : when(c.lastSyncedAt ?? c.createdAt)}</DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        </DataTable>
      </section>

      {overview && hasInstantly && <BreakdownTable breakdowns={overview.breakdowns} provider="instantly" />}
    </div>
  );
}

/** A campaign's sequence in one line: a bar plus the stages that have anyone in them. */
function SequenceSummary({ progress, stepCount }: { progress: SequenceProgress; stepCount?: number | null }): JSX.Element {
  const parts = sequenceStages(progress, stepCount)
    .filter((s) => s.count > 0)
    .map((s) => `${s.key === 'finished' ? 'Done' : s.key === 'not_sent' ? 'Waiting' : s.label.replace('On email', 'Email')}: ${s.count.toLocaleString()}`);
  return (
    <div className="space-y-1">
      <SequenceBar progress={progress} stepCount={stepCount} />
      <div className="text-sm text-muted-foreground">{parts.join(' · ') || 'Nobody yet'}</div>
    </div>
  );
}
